import React, { useContext } from 'react';
import {
    useMutation, useReactiveVar, useApolloClient,
    gql,
} from '@apollo/client';
import queryString from 'query-string';

/* */
import { analyticService } from 'global/services';
import { removeSchemaParams } from 'app/utils/basketSchema';

/* FETCH */
import {
    deliveryIDVar,
    isGlobalLoadingVar,
    selectedPeriodVar,
    subscriptionTypeVar,
} from 'app/apollo/reaction';
import { useRouterState } from 'app/hooks/useRouterState';
import { useSetsCartMutations } from 'app/views/Main/hooks/useSetsCartMutations';
import { PathSegmentContext } from 'app/views/Main/PathSegment.context';
import { DELIVERY_BASKET_QUERY } from 'app/graphql/network/deliveryCart';
import { sleep } from 'global/utils';
import {
    DISH_FIELDS_FOR_GA_FRAGMENT,
    MUTATE_CART_FROM_DELIVERY_MENU,
    MUTATE_CART_FROM_MEMU,
} from './grapql/useAddToBasketMutation.graphql';

// import {
//     MUTATE_CART_FROM_DELIVERY_BASKET,
// } from './graphql/useCartMutationInBasket.graphql';

type handleAddToBasketMutationProps = {
    dishId: string
    portion: number
    deliveryId?: string | null,
}

type handleAddToBasketMutationByIdListProps = {
    dishes: {
        dishId: string
        portion: number
    }[],
    deliveryId?: string | null,
}

type TonBasketUpdatedAction = {
    dishId: string
}

const useOnBasketUpdatedAction = () => {
    const subscriptionType = useReactiveVar(subscriptionTypeVar);
    const client = useApolloClient();
    const [routerState] = useRouterState();
    const period = useReactiveVar(selectedPeriodVar);
    const { history, location } = routerState;
    const { location: { search } } = window;

    const urlParams = new URLSearchParams(search);

    const onBasketUpdatedAction = ({ dishId }: TonBasketUpdatedAction) => {
        const dishDataForGA = client.readFragment({
            id: `menuDish:${dishId}`,
            fragment: DISH_FIELDS_FOR_GA_FRAGMENT,
        }) || {};

        const portionsInCart = client.readFragment({
            id: `cart:new:${period}`,
            fragment: gql`
                fragment GET_PORTIONS_IN_CART on cart {
                    sections {
                        id
                        items {
                            dish_id
                            portions
                        }
                        cartType
                    }
                }
            `,
        });
        const addedDishPortions = portionsInCart.sections[0]
            ?.items
            // @ts-ignore
            .find(({ dish_id }) => dish_id === dishId)?.portions || 0;

        // Удаляет query параметры dish=xxxx.xxxx из url, если они есть
        if (urlParams.get('dishes')) {
            const nextSearch = removeSchemaParams(search);
            history.push({
                ...location,
                search: nextSearch,
            });
        }

        const source = location.pathname.includes('/basket')
            ? 'basket'
            : 'menu';
        // @ts-ignore
        window?.dataLayer.push({ ecommerce: null });
        analyticService
            .push({
                eventName: 'add_to_cart',
                dishes: [{ ...dishDataForGA, portion: addedDishPortions }],
                typeOfSet: 'regular',
                source: location.pathname.includes('upsell') ? 'screen_after_basket' : source,
                subscriptionType,
            });
    };
    return { onBasketUpdatedAction };
};

export function useAddToBasketMutation() {
    const { onBasketUpdatedAction } = useOnBasketUpdatedAction();
    const [checkSetsParams, removeSetsParams] = useSetsCartMutations();
    const deliveryID = useReactiveVar(deliveryIDVar);

    const { segment } = useContext(PathSegmentContext);
    const OPERATION = deliveryID ? MUTATE_CART_FROM_DELIVERY_MENU : MUTATE_CART_FROM_MEMU;

    // MUTATE_CART_FROM_DELIVERY_BASKET
    const selectedPeriod = useReactiveVar(selectedPeriodVar);
    const [mutateCart, { loading: basketLoading }] = useMutation(OPERATION, {
        variables: {
            period: selectedPeriod,
        },
        context: {
            message: 'menu:mutate:useAddToBasketMutation',
        },
        refetchQueries: () => {
            if (segment === 'delivery') {
                return [
                    {
                        query: DELIVERY_BASKET_QUERY,
                        context: {
                            message: 'root:refetch:useCartMutationInBasket:Delivery',
                        },
                        variables: {
                            deliveryId: deliveryID,
                        },
                    },
                ];
            }
            return [];
        },
        awaitRefetchQueries: segment === 'delivery',
    });

    const handleAddToBasketMutation = (
        { dishId, portion, deliveryId }: handleAddToBasketMutationProps,
    ) => {
        const params = queryString.parse(window.location.search);
        const subdomain = window.location.pathname?.replace(/^\/+|\/+$/g, '');
        const isSelection = 'st' in params;
        let sourceName;
        if (isSelection && subdomain.includes('basket')) {
            sourceName = 'basket';
        }

        if (isSelection && !subdomain.includes('basket')) {
            sourceName = 'collections';
        }

        if (subdomain.includes('upsell')) {
            sourceName = 'screen_after_basket';
        }

        isGlobalLoadingVar(true);
        mutateCart({
            variables: {
                dishes: [{ dish_id: dishId, portions: portion }],
                delivery_id: deliveryId,
                source_name: sourceName || window.location.pathname?.replace(/^\/+|\/+$/g, ''),
            },
            onCompleted: () => {
                const isSetCart = checkSetsParams();
                if (isSetCart) {
                    removeSetsParams();
                }
                onBasketUpdatedAction({ dishId });
            },
        }).finally(() => { sleep(200).then(() => isGlobalLoadingVar(false)); });
    };

    const handleAddToBasketByidListMutation = (
        { dishes, deliveryId }: handleAddToBasketMutationByIdListProps,
    ) => {
        const params = queryString.parse(window.location.search);
        const subdomain = window.location.pathname?.replace(/^\/+|\/+$/g, '');
        const isSelection = 'st' in params;
        let sourceName;
        if (isSelection && subdomain.includes('basket')) {
            sourceName = 'basket';
        }

        if (isSelection && !subdomain.includes('basket')) {
            sourceName = 'collections';
        }

        isGlobalLoadingVar(true);
        mutateCart({
            variables: {
                dishes,
                delivery_id: deliveryId,
                source_name: sourceName || window.location.pathname?.replace(/^\/+|\/+$/g, ''),
            },
            onCompleted: () => {
                const isSetCart = checkSetsParams();
                if (isSetCart) {
                    removeSetsParams();
                }
                // onBasketUpdatedAction({ dishId });
            },
        }).finally(() => { sleep(200).then(() => isGlobalLoadingVar(false)); });
    };

    return { handleAddToBasketMutation, handleAddToBasketByidListMutation, basketLoading };
}
